/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useEffect, useState } from "react";

import {
    Navigate,
    Route,
    Routes,
    useLocation,
    useNavigate,
} from "react-router-dom";
import { redirects, protectedRoutes, routes, paths } from "ui/routes";
import {
    Toast,
    Sidebar,
    LivestreamPlayer,
    TrialEndedPopup,
} from "ui/components";
import { renewAccessToken } from "core/services";
import { useNotifications } from "ui/context/NotificationsContext";
import { Loading } from "ui/views";
import { useLivestreamContext } from "ui/context/LivestreamContext";
import { useSessionContext } from "ui/context/SessionContext";
import styles from "./App.module.scss";

const App: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { notifications, removeNotification } = useNotifications();
    const { channels, removeAll: removeAllLivestreams } =
        useLivestreamContext();
    const sessionContext = useSessionContext();
    const [pending, setPending] = useState<string | null>(null);

    const plan = sessionContext.selectedProject?.package ?? "trial";

    const authenticate = useCallback(async () => {
        const { session } = sessionContext;
        if (!session) {
            setPending(null);
            return;
        }

        const thisInstant = Date.now();
        const oneDayInMillis = 60 * 60 * 24 * 1000;

        const { refreshCredentials } = session;

        const sessionInstant = Date.parse(session.expiryDate.toString());
        const refreshInstant = Date.parse(
            refreshCredentials.expiryDate.toString(),
        );

        if (Number.isNaN(sessionInstant) || Number.isNaN(refreshInstant)) {
            sessionContext.setSession(null);
            setPending(null);
            return;
        }

        const sessionDiff = sessionInstant - thisInstant;
        const refreshDiff = refreshInstant - thisInstant;

        if (sessionDiff > oneDayInMillis) {
            setPending(null);
            return;
        }

        if (refreshDiff < 10 * 1000) {
            sessionContext.setSession(null);
            setPending(null);
            return;
        }

        setPending("Loading…");
        renewAccessToken(refreshCredentials.token)
            .then((newSession) => {
                sessionContext.setSession(newSession);
                setPending(null);
            })
            .catch(() => {
                removeAllLivestreams();
                sessionContext.setSession(null);
                navigate(paths.login, { replace: true });
            });
    }, [sessionContext]);
    React.useEffect(() => {
        authenticate();
    }, [authenticate]);

    useEffect(() => {
        const handleNavigation = () => {
            const publicPaths = [
                paths.register,
                paths.login,
                paths.error,
                paths.verify,
                paths.activatePage,
                paths.notVerified,
                "/resetpassword/",
            ];

            const isPublicPath = publicPaths.some((path) =>
                location.pathname.startsWith(path),
            );

            if (isPublicPath) {
                return;
            }

            if (
                sessionContext.session &&
                !sessionContext.verified &&
                !location.pathname.startsWith("/notverified") &&
                !location.pathname.startsWith("/register") &&
                !location.pathname.startsWith("/verify")
            ) {
                navigate(paths.notVerified, { replace: true });
            } else if (
                sessionContext.session &&
                !sessionContext.selectedProject?.id &&
                !location.pathname.startsWith("/notverified") &&
                !location.pathname.startsWith("/projects") &&
                !location.pathname.startsWith("/register") &&
                !location.pathname.startsWith("/oauth") &&
                !location.pathname.startsWith("/verify") &&
                !location.pathname.startsWith("/activate") &&
                !location.pathname.startsWith("/livestream") &&
                !location.pathname.startsWith("/login") &&
                !location.pathname.startsWith("/error")
            ) {
                navigate(paths.projectsPage, { replace: true });
            } else if (!sessionContext.session && !isPublicPath) {
                navigate(paths.login, { replace: true });
            }
        };

        handleNavigation();
    }, [
        sessionContext.session,
        sessionContext.verified,
        sessionContext.selectedProject?.id,
        location.pathname,
        navigate,
    ]);

    return (
        <>
            <Toast.Container className={styles.toast__container}>
                {notifications.map((notification) => (
                    <Toast
                        key={notification.id}
                        {...notification}
                        removeToast={removeNotification}
                    />
                ))}
            </Toast.Container>
            {channels.map((c) => (
                <LivestreamPlayer key={c.key} channel={c} />
            ))}
            {plan === "disabled" &&
                !location.pathname.startsWith("/projects") && (
                    <TrialEndedPopup />
                )}
            <div className={styles.wrapper}>
                {sessionContext.session &&
                    sessionContext.selectedProject?.id &&
                    !location.pathname.startsWith("/livestream") &&
                    !location.pathname.startsWith("/error") && (
                        <Sidebar className={styles.sidebar} />
                    )}
                <div className={styles.main}>
                    {pending ? (
                        <Loading text={pending} />
                    ) : (
                        <Routes>
                            {routes.map((route) => (
                                <Route key={route.path} {...route} />
                            ))}
                            {sessionContext.session &&
                                protectedRoutes.map((route) => (
                                    <Route
                                        key={route.path}
                                        path={route.path}
                                        element={route.element}
                                    />
                                ))}
                            {redirects.map((redirect) => (
                                <Route
                                    key={redirect.from}
                                    path="*"
                                    element={
                                        <Navigate to={redirect.to} replace />
                                    }
                                />
                            ))}
                            <Route
                                path={`${paths.login}?origin=${location.pathname}`}
                            />
                        </Routes>
                    )}
                </div>
            </div>
        </>
    );
};

export default App;
